import { useState, useEffect } from 'react';
import {
    AppShell, Burger, Button, Group, Title, SimpleGrid, Text,
    Container, Divider, ScrollArea, Skeleton, Tabs, Image
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { AdminWinnersTab } from "./admin-page-components/AdminWinnersTab";
import config from "../config";
import api from '../api';
import {useAuth} from "../hooks/useAuth";


interface Campaign {
    id: number;
    pid: string;
    name: string;
    status: 'active' | 'inactive';
    startDate: string;
    endDate: string;
    participants: number;
}

export function AdminPage() {
    const [opened, { toggle }] = useDisclosure();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const { logout } = useAuth();


    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const response = await api.get(`${config.BASE_URL}/public/campaign/all?page=0&size=100`);

                const data = response.data;

                const campaignData = data.content.map((item: any, index: number) => ({
                    id: index,
                    pid: item.campaignPid,
                    name: item.name,
                    status: new Date(item.endDate) > new Date() ? 'active' : 'inactive',
                    startDate: '',
                    endDate: item.endDate,
                    participants: 0,
                }));

                setCampaigns(campaignData);
            } catch (error) {
                console.error('Error loading campaigns:', error);
            }
        };

        fetchCampaigns();
    }, []);

    const handleCampaignSelect = async (campaign: Campaign) => {
        setSelectedCampaign(campaign);

        try {
            const response = await api.get(`${config.BASE_URL}/public/campaign`, {
                params: { campaignPid: campaign.pid }
            });

            const data = response.data;
            setSelectedCampaign(prev => ({
                ...prev!,
                startDate: data.startDate,
                participants: data.participants,
            }));
        } catch (error) {
            console.error('Error loading campaign details:', error);
        }
    };

    return (
        <AppShell
            header={{ height: 80 }}
            navbar={{ width: 300, breakpoint: 'sm', collapsed: { mobile: !opened } }}
            padding="md"
        >
            <AppShell.Header>
                <Group h="100%" justify="space-between" className="py-2 px-5 bg-green-100">
                    <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
                    <Image src="/assets/promeks_logo.png" className="h-full" />
                    <Button onClick={logout}>Logout</Button>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p="md">
                <Title order={3}>Kampanje</Title>
                <Divider my="lg" />
                <ScrollArea>
                    <SimpleGrid cols={1} spacing="xs">
                        {campaigns.map(campaign => (
                            <Container
                                key={campaign.id}
                                onClick={() => handleCampaignSelect(campaign)}
                                p="md"
                                style={{
                                    backgroundColor: selectedCampaign?.id === campaign.id ? '#e6fffa' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                        selectedCampaign?.id === campaign.id ? '#D1FAE5' : '#f0fdfa';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                        selectedCampaign?.id === campaign.id ? '#D1FAE5' : 'transparent';
                                }}
                            >
                                <Title order={5}>{campaign.name}</Title>
                                <Group>
                                    <div
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            marginLeft: 8,
                                            backgroundColor: campaign.status === 'active' ? '#48bb78' : '#f56565',
                                        }}
                                    />
                                    <Text>{campaign.status}</Text>
                                </Group>
                                <Text><b>Exp:</b> {new Date(campaign.endDate).toDateString()}</Text>
                            </Container>
                        ))}
                    </SimpleGrid>
                </ScrollArea>
            </AppShell.Navbar>

            <AppShell.Main>
                {selectedCampaign ? (
                    <>
                        <div className="flex flex-col justify-center items-center p-5">
                            <Title order={6}>KAMPANJA</Title>
                            <br />
                            <Title order={1}>"  {selectedCampaign.name}  "</Title>
                        </div>
                        <Tabs variant="outline" defaultValue="overview">
                            <Tabs.List>
                                <Tabs.Tab value="overview">Statistika</Tabs.Tab>
                                <Tabs.Tab value="winners">Pobednici</Tabs.Tab>
                                <Tabs.Tab value="participants">Ucesnici</Tabs.Tab>
                                <Tabs.Tab value="settings">Podesavanja</Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="overview">
                                <div className="w-full flex">
                                    <div className="w-1/2 p-5">
                                        <Text>Status: {selectedCampaign.status}</Text>
                                        <Text>Datum pocetka: {new Date(selectedCampaign.endDate).toLocaleDateString()}</Text>
                                        <Text>Datum zavrsetka: {new Date(selectedCampaign.startDate).toLocaleDateString()}</Text>
                                    </div>
                                    <div className="w-1/2 p-5 flex flex-col justify-center items-center">
                                        <div className="m-5 p-24 w-3/5 flex flex-col justify-center items-center border-gray-800 border-4 rounded-2xl bg-white">
                                            <p className="text-8xl">{selectedCampaign.participants}</p>
                                            <p>prijave</p>
                                        </div>
                                    </div>
                                </div>
                            </Tabs.Panel>

                            <Tabs.Panel value="winners">
                                <AdminWinnersTab campaignPid={selectedCampaign.pid} />
                            </Tabs.Panel>
                            <Tabs.Panel value="participants">In development...</Tabs.Panel>
                            <Tabs.Panel value="settings">In development...</Tabs.Panel>
                        </Tabs>
                    </>
                ) : (
                    <div>
                        <Text>Izaberite kampanju da biste videli detalje.</Text>
                        <Skeleton height={25} mt={6} width="70%" radius="xl" />
                        <Skeleton height={25} mt={6} width="70%" radius="xl" />
                        <Skeleton height={25} mt={6} width="70%" radius="xl" />
                        <Skeleton height={25} mt={6} width="70%" radius="xl" />
                    </div>
                )}
            </AppShell.Main>
        </AppShell>
    );
}
